import classNames from 'classnames';
import type { CSSProperties, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';

import { Horizontal, Vertical } from '#src/atoms/layout/flex/index.tsx';
import { BrandL, BrandXXL, LabelL } from '#src/atoms/typography/index.tsx';
import { Fan } from '#src/components/animations/Fan/index.tsx';
import { SetColorTheme } from '#src/set/SetColorTheme/index.tsx';

import { Padlock } from './Padlock/index.tsx';

const shineAnimationDuration = 2000;

const scaleAnimation = keyframes`
  0% {
    transform: scale(1.67);
  }
  100% {
    transform: scale(1);
  }
`;
const translateAnimation = (translateAmount: string) => keyframes`
  0% {
    transform: translateY(${translateAmount});
  }
  100% {
    transform: translateY(0px);
  }
`;
const disappear = keyframes`
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
`;
const appear = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;
const shineAnimation = keyframes`
  0% {
    transform: translate(-50%, -50%);
  }
  25% {
    transform: translate(50%, 50%);
  }
  100% {
    transform: translate(50%, 50%);
  }
`;
const Root = styled(Vertical).attrs({ gap: 3, center: true })`
  --image-size: calc(22 * var(--unit));
  padding-bottom: calc(8 * var(--unit));
`;
const ImgsArea = styled(Horizontal).attrs({ center: true })`
  position: relative;
  isolation: isolate;
  transform: translateY(60px);
  flex-wrap: wrap;

  &.translate {
    animation: ${translateAnimation('60px')} 0.6s var(--ease-in-out-quart)
      forwards;
    animation-delay: 0.5s;
  }
`;

const ImgArea = styled(Horizontal).attrs({ center: true })`
  position: relative;
  isolation: isolate;
  width: var(--image-size);
  height: var(--image-size);
`;

const Img = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
`;
const BrightImg = styled(Img)`
  position: absolute;
  inset: 0;
  filter: brightness(15) blur(2px);
  opacity: 0;

  &.animate {
    animation: ${disappear} 0.4s ease-out forwards;
  }
`;
const PadlockWrapper = styled.div`
  position: absolute;
  inset: 0;
`;
const RewardImgWrapper = styled.div`
  isolation: isolate;
  position: relative;
  opacity: 0;
  transform: scale(1.67);
  width: 100%;
  height: 100%;
  &.revealed {
    opacity: 1;
    animation: ${scaleAnimation} 0.6s var(--ease-in-out-quart) forwards;
  }
`;
const TextWrapper = styled(Vertical).attrs({ gap: 2, center: true })`
  transform: translateY(150px);
  &.translate {
    animation: ${translateAnimation('150px')} 0.65s var(--ease-in-out-quart)
      forwards;
    animation-delay: 0.55s;
  }
`;
const Title = styled(BrandL)`
  opacity: 0;
  text-wrap: pretty;
  &.revealed {
    animation: ${appear} 0.65s var(--ease-in-out-quart) forwards;
    animation-delay: 0.55s;
  }
`;
const Subtitle = styled(LabelL).attrs({ color: 'var(--c-nd-600)' })`
  opacity: 0;
  text-wrap: pretty;
  &.revealed {
    animation: ${appear} 0.65s var(--ease-in-out-quart) forwards;
    animation-delay: 0.6s;
  }
`;
const Amount = styled(BrandXXL)`
  opacity: 0;
  &.revealed {
    animation: ${appear} 0.65s var(--ease-in-out-quart) forwards;
    animation-delay: 0.6s;
  }
`;
export const RewardImageShine = styled.div`
  opacity: 0;
  position: absolute;
  inset: 0;
  z-index: 1;
  width: 100%;
  height: 100%;
  mask-image: var(--mask-image);
  mask-size: contain;
  mask-repeat: no-repeat;
  mask-position: center;
  &.revealed {
    opacity: 1;
  }

  &.revealed::before {
    content: '';
    z-index: 0;
    display: block;
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      to bottom right,
      transparent 35%,
      rgba(255, 255, 255, 0.3) 50%,
      transparent 65%
    );
    transform-origin: center;
    animation: ${shineAnimation} ${shineAnimationDuration}ms linear infinite;
  }
`;

const ChildrenContainer = styled.div`
  opacity: 0;
  width: 100%;
  &.revealed {
    animation: ${appear} 0.65s var(--ease-in-out-quart) forwards;
    animation-delay: 0.6s;
  }
`;

type Props = {
  imgUrls: string[];
  title: ReactNode;
  subtitle: ReactNode;
  amount?: ReactNode;
  withPadlock?: boolean;
  style?: React.CSSProperties;
  onAnimationEnd?: () => void;
  children?: ReactNode;
  shouldUseSetBackground?: boolean;
};
export const RewardUnlockedAnimation = ({
  imgUrls,
  title,
  subtitle,
  amount,
  withPadlock,
  onAnimationEnd,
  style,
  children,
  shouldUseSetBackground,
}: Props) => {
  const [animationStep, setAnimationStep] = useState<
    'padlock' | 'image' | 'shine'
  >(withPadlock ? 'padlock' : 'image');
  const [timeoutRef, setTimeoutRef] = useState<NodeJS.Timeout | null>(null);

  const showReward = animationStep !== 'padlock';

  useEffect(() => {
    if (timeoutRef) {
      clearTimeout(timeoutRef);
    }
  }, [timeoutRef]);

  return (
    <Root gap={3} className="text-center" style={style}>
      <Vertical gap={2}>
        <ImgsArea className={classNames({ translate: showReward })}>
          <Fan>
            {imgUrls.map(imgUrl => (
              <ImgArea key={imgUrl}>
                {animationStep === 'padlock' && (
                  <PadlockWrapper>
                    <Padlock onAnimationEnd={() => setAnimationStep('image')} />
                  </PadlockWrapper>
                )}
                <RewardImgWrapper
                  className={classNames({ revealed: showReward })}
                >
                  <RewardImageShine
                    className={classNames({
                      revealed: animationStep === 'shine',
                    })}
                    style={
                      { '--mask-image': `url(${imgUrl})` } as CSSProperties
                    }
                  />
                  <Img src={imgUrl} alt="" />
                  <BrightImg
                    src={imgUrl}
                    alt=""
                    className={classNames({ animate: showReward })}
                  />
                </RewardImgWrapper>
              </ImgArea>
            ))}
          </Fan>
        </ImgsArea>
        <SetColorTheme disabled={!shouldUseSetBackground}>
          <TextWrapper className={classNames({ translate: showReward })}>
            {!!amount && (
              <Amount
                color="var(--c-nd-600)"
                className={classNames({ revealed: showReward })}
              >
                {amount}
              </Amount>
            )}
          </TextWrapper>
        </SetColorTheme>
      </Vertical>
      <SetColorTheme disabled={!shouldUseSetBackground}>
        <TextWrapper
          className={classNames({ translate: showReward })}
          onAnimationEnd={() => {
            setAnimationStep('shine');
            if (onAnimationEnd) {
              const timeout = setTimeout(
                onAnimationEnd,
                shineAnimationDuration / 2
              );
              setTimeoutRef(timeout);
            }
          }}
        >
          <Title
            color="var(--c-white)"
            className={classNames({ revealed: showReward })}
          >
            {title}
          </Title>
          {!!subtitle && (
            <Subtitle
              color="var(--c-white)"
              className={classNames({ revealed: showReward })}
            >
              {subtitle}
            </Subtitle>
          )}
          {!!children && (
            <ChildrenContainer className={classNames({ revealed: showReward })}>
              {children}
            </ChildrenContainer>
          )}
        </TextWrapper>
      </SetColorTheme>
    </Root>
  );
};
